import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const SideFormPopout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    comments: "",
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke("submit-contact", {
        body: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          project_description: formData.comments || null,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Submission failed");
      }

      const data = response.data;
      
      if (data?.error) {
        if (data.resetIn) {
          const minutes = Math.ceil(data.resetIn / 60);
          throw new Error(`Too many submissions. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
        }
        throw new Error(data.error);
      }

      toast({
        title: "Request Submitted",
        description: "We'll get back to you shortly!",
      });
      setFormData({ firstName: "", lastName: "", phone: "", email: "", comments: "" });
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Tab Button - Fixed on right side */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-primary text-primary-foreground py-4 px-2 rounded-l-lg shadow-lg transition-transform duration-300 hover:-translate-x-1 ${
          isOpen ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
        aria-label="Request an Appointment"
      >
        <span className="text-sm font-semibold tracking-wide">
          Request an Appointment
        </span>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-out Panel */}
      <div
        className={`fixed right-0 top-24 w-80 md:w-96 bg-primary z-50 shadow-2xl rounded-l-xl transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="relative p-4">
          {/* Close Button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-0 right-0 text-primary-foreground hover:opacity-70 transition-opacity p-1"
            aria-label="Close form"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Form Content */}
          <div className="mt-2">
            <h2 className="text-xl font-bold text-primary-foreground mb-1">
              Request an Appointment
            </h2>
            <p className="text-primary-foreground/90 text-sm mb-4">
              Call (210) 361-1216 or fill out the form below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                className="bg-background/90 border-0 placeholder:text-muted-foreground h-9"
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                className="bg-background/90 border-0 placeholder:text-muted-foreground h-9"
              />
              <Input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-background/90 border-0 placeholder:text-muted-foreground h-9"
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-background/90 border-0 placeholder:text-muted-foreground h-9"
              />
              <Textarea
                placeholder="Comments"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                rows={2}
                className="bg-background/90 border-0 placeholder:text-muted-foreground resize-none"
              />
              <Button
                type="submit"
                variant="secondary"
                className="w-full font-semibold h-9"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Request Appointment"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideFormPopout;
